/**
 * Reads the configured country codes defensively. A misconfigured setup.json -
 * or an env var override - can deliver a plain string like "DE" or "DE,AT"
 * instead of a list. Anything unusable is treated as "no restriction" instead
 * of breaking the country picker. Codes are upper cased and deduplicated so
 * that the setup may spell them however it likes.
 */
function toCountryCodeList(value: unknown): string[] {
  const rawCodes = typeof value === 'string' ? value.split(',') : value;

  if (!Array.isArray(rawCodes)) {
    return [];
  }

  const countryCodes = rawCodes
    .filter(code => typeof code === 'string')
    .map(code => code.trim().toUpperCase())
    .filter(code => code.length > 0);

  // Deduplicated so that a setup listing e.g. ["DE", "de"] does not make the
  // country picker count one country twice.
  return [...new Set(countryCodes)];
}

/**
 * Narrows the country list returned by the address API down to what the current
 * setup wants to offer. The API stays authoritative for what the address
 * provider can actually serve - the setup only restricts it further, so no
 * country can be offered that would then return no results.
 */
export function useSupportedAddressSearchCountries(): { filterCountryCodes: (countryCodesFromApi: string[]) => string[] } {
  const config = useRuntimeConfig();

  const supportedCountryCodes = toCountryCodeList(config.public.supportedAddressSearchCountries);

  // Order follows the setup so the branding also controls the order in the picker.
  function filterCountryCodes(countryCodesFromApi: string[]): string[] {
    if (supportedCountryCodes.length === 0) {
      return countryCodesFromApi;
    }

    // Matched case insensitively, but returned in the spelling of the API -
    // that is the value the address search sends back to the backend.
    return supportedCountryCodes
      .map(supportedCode => countryCodesFromApi.find(code => code.toUpperCase() === supportedCode))
      .filter(code => code !== undefined);
  }

  return { filterCountryCodes };
}
